"use client";

import { DrupalNode } from "next-drupal";
import Link from "next/link";
import { motion } from "framer-motion";
import { IoMdImages } from "react-icons/io";
import ReactPlayer from "react-player";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import { FaInstagram } from "react-icons/fa6";
import { MdChevronRight } from "react-icons/md";
import Banner from "@/components/sections/Banner";
import { config } from "@/lib/config";

interface CountryDetailProps {
  slugTitle: string;
  data: DrupalNode[];
}

function ArticleDetails({ slugTitle, data }: CountryDetailProps) {
  const filterData = data.filter((item) => item.id === slugTitle);
  const article = filterData[0];

  return (
    <div className="pt-20">
      <Banner
        title={article?.title}
        isRounded
        backgroundImg="/static/images/india.webp"
      />
      {article?.field_cs_description?.processed && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          dangerouslySetInnerHTML={{
            __html: article?.field_cs_description?.processed,
          }}
          className="max-w-[900px] mx-auto text-medium text-[#003350] --font-poppins leading-8 mobileMax:text-xsmall mobileMax:leading-normal text-center pb-4 pt-[100px]"
        />
      )}

      {article?.field_cs_landing_page_cards.length > 0 && (
        <div className="overflow-visible">
          <div className="py-[100px] bg-mapGray mobileMax:py-8">
            <div className="mini-container z-[2]">
              <div className="text-medium text-[#003350] --font-poppins leading-8 mobileMax:text-xsmall mobileMax:leading-normal">
                <div className="flex gap-8 mobileMax:flex-col-reverse mobileMax:gap-4 items-start relative">
                  {/* Cards Section - Left Side */}
                  <div className="flex-1 mobileMax:w-full">
                    <div className="flex flex-col gap-[30px] mobileMax:gap-4">
                      {article?.field_cs_landing_page_cards?.map(
                        (card: any, index: number) => {
                          const isVideo =
                            card?.field_link.includes("youtube.com") ||
                            card?.field_link.includes("youtu.be");
                          const backgroundImage =
                            card?.field_image_icon?.uri?.url;

                          return (
                            <Link
                              key={card?.id || index}
                              className="cursor-pointer w-full max-w-[600px] transition-all duration-700 ease-in-out hover:scale-[1.02] hover:shadow-lg"
                              href={card?.field_link || "#"}
                              target="_blank"
                            >
                              <motion.div
                                className="bg-white flex flex-col h-full rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 relative overflow-hidden group !min-h-[300px] min-h-[255px] mobileMax:!min-h-[200px] mobileMax:min-h-[180px]"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                  duration: 0,
                                  border: {
                                    duration: 1.8,
                                    ease: "easeInOut",
                                  },
                                }}
                              >
                                {card?.field_cs_theme && (
                                  <div className="absolute top-4 left-4 z-[1] mobileMax:top-2 mobileMax:left-2">
                                    <span className="bg-white/90 text-cardHeading px-3 py-1 text-sm mobileMax:text-xs mobileMax:px-2 mobileMax:py-0.5 inline-block font-semibold rounded">
                                      {card?.field_cs_theme}
                                    </span>
                                  </div>
                                )}

                                {/* Background Image */}
                                {!isVideo && backgroundImage && (
                                  <div
                                    className="absolute inset-0 w-full h-full"
                                    style={{
                                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2)), url(${config.apiBase}${backgroundImage})`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                      backgroundRepeat: "no-repeat",
                                    }}
                                  />
                                )}

                                {/* Placeholder for no image */}
                                {!isVideo && !backgroundImage && (
                                  <div className="absolute inset-0 w-full h-full bg-graybg flex items-center justify-center">
                                    <IoMdImages className="text-gray-400 w-16 h-16 mobileMax:w-12 mobileMax:h-12" />
                                  </div>
                                )}

                                {/* Video */}
                                {isVideo && (
                                  <div className="absolute inset-0 w-full h-full z-[1]">
                                    <ReactPlayer
                                      url={card?.field_link || ""}
                                      width="100%"
                                      height="100%"
                                      light={true}
                                      controls={true}
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                      }}
                                    />
                                  </div>
                                )}

                                {/* Content Overlay */}
                                <div className="absolute bottom-0 left-0 p-4 md:p-6 mobileMax:p-3 w-full z-[2] bg-gradient-to-t from-black/60 to-transparent">
                                  <div className="max-w-full">
                                    <h3
                                      className={`${
                                        backgroundImage || isVideo
                                          ? "text-white"
                                          : "text-cardHeading"
                                      } text-left font-bold !text-[20px] mobileMax:!text-[16px] leading-tight mb-2`}
                                    >
                                      {card?.title || `Untitled ${index + 1}`}
                                    </h3>
                                  </div>
                                </div>

                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              </motion.div>
                            </Link>
                          );
                        },
                      )}
                    </div>
                  </div>

                  {/* Share Section - Right Side */}
                  <div className="w-80 mobileMax:w-full mobileMax:mt-6 flex-shrink-0 mobileMax:order-first">
                    <div className="bg-white shadow-md p-5 mobileMax:p-4 rounded-xl">
                      <h5 className="text-numans text-xmedium leading-normal mb-3 text-[#003350] font-bold mobileMax:text-small">
                        Share:
                      </h5>
                      <div className="flex items-center mb-8 mobileMax:mb-4 flex-wrap gap-2">
                        <EmailShareButton
                          url={
                            typeof window !== "undefined"
                              ? `${window?.origin}${"path"}` || "#"
                              : "#"
                          }
                          className="mr-2"
                        >
                          <EmailIcon size={32} round />
                        </EmailShareButton>
                        <FacebookShareButton
                          url={
                            typeof window !== "undefined"
                              ? `${window?.origin}${"path"}` || "#"
                              : "#"
                          }
                          className="mx-2"
                        >
                          <FacebookIcon size={32} round />
                        </FacebookShareButton>
                        <TwitterShareButton
                          url={
                            typeof window !== "undefined"
                              ? `${window?.origin}${"path"}` || "#"
                              : "#"
                          }
                          className="mx-2"
                        >
                          <TwitterIcon size={32} round />
                        </TwitterShareButton>
                        <FaInstagram
                          size={32}
                          className="insta-gradient-color text-white rounded-[7px] p-0.5 block mx-2 cursor-pointer"
                        />
                        <WhatsappShareButton
                          url={
                            typeof window !== "undefined"
                              ? `${window?.origin}${"path"}` || "#"
                              : "#"
                          }
                          className="mx-2"
                        >
                          <WhatsappIcon size={32} round />
                        </WhatsappShareButton>
                      </div>
                      <Link
                        href={
                          article?.field_cs_landing_page_cards[0]?.field_link ||
                          "#"
                        }
                        target="_blank"
                        className="w-full flex-1 flex items-center rounded-[23px] justify-center text-small mobileMax:text-xs bg-[linear-gradient(to_right,#316FA8,#003350)] text-white font-medium --font-poppins py-1.5 px-2.5 mobileMax:py-2 mobileMax:px-3 mb-2"
                      >
                        Learn more
                        <MdChevronRight className="w-[18px] h-[18px] mobileMax:w-[16px] mobileMax:h-[16px] inline-block max-w-[18px] ml-0.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleDetails;
